import type { FiguraConfig } from '@/types';

// Servicio para gestionar sesiones temporales de usuarios no logueados
// Los datos se guardan en localStorage con un ID de sesion unico
// y se sincronizarian con Firebase en produccion

const SESSION_KEY = 'funcomunion_temp_session';
const SESSION_EXPIRY_DAYS = 30;

interface TempSession {
  id: string;
  createdAt: number;
  lastActivity: number;
  fotoReferencia?: string;
  config?: Partial<FiguraConfig>;
  pedidoCompletado: boolean;
}

// Escribe la sesión aguantando que localStorage esté lleno. Si no cabe, se
// suelta primero la configuración (se puede rehacer) y después la foto, antes
// que dejar la escritura a medias o lanzar una excepción en mitad de una
// subida.
function guardarSesion(session: TempSession): boolean {
  const intentos: TempSession[] = [
    session,
    { ...session, config: undefined },
    { ...session, config: undefined, fotoReferencia: undefined },
  ];
  for (const intento of intentos) {
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(intento));
      return true;
    } catch {
      // cuota llena: probamos con menos datos
    }
  }
  return false;
}

// Generar ID unico para sesion temporal
function generateSessionId(): string {
  return 'temp_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

// Obtener o crear sesion temporal
export function getOrCreateTempSession(): TempSession {
  const stored = localStorage.getItem(SESSION_KEY);
  
  if (stored) {
    const session: TempSession = JSON.parse(stored);
    // Actualizar ultima actividad
    session.lastActivity = Date.now();
    guardarSesion(session);
    return session;
  }
  
  // Crear nueva sesion
  const newSession: TempSession = {
    id: generateSessionId(),
    createdAt: Date.now(),
    lastActivity: Date.now(),
    pedidoCompletado: false,
  };
  
  guardarSesion(newSession);
  return newSession;
}

// Guardar foto en sesion temporal
export function saveTempFoto(fotoBase64: string): void {
  const session = getOrCreateTempSession();
  session.fotoReferencia = fotoBase64;
  session.lastActivity = Date.now();
  const guardada = guardarSesion(session);
  
  // En produccion: tambien subir a Firebase Storage en /temp/{session.id}/foto.jpg
  if (!guardada) console.warn('Sin espacio para guardar la foto en la sesion temporal:', session.id);
}

// Guardar configuracion en sesion temporal
export function saveTempConfig(config: Partial<FiguraConfig>): void {
  const session = getOrCreateTempSession();
  session.config = { ...session.config, ...config };
  session.lastActivity = Date.now();
  guardarSesion(session);
}

// Obtener datos de sesion temporal
export function getTempSession(): TempSession | null {
  const stored = localStorage.getItem(SESSION_KEY);
  if (!stored) return null;

  let session: TempSession;
  try {
    session = JSON.parse(stored) as TempSession;
  } catch {
    // Escritura truncada por falta de espacio: la sesión ya no sirve.
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
  
  // Verificar si expiro (30 dias)
  const daysSinceActivity = (Date.now() - session.lastActivity) / (1000 * 60 * 60 * 24);
  if (daysSinceActivity > SESSION_EXPIRY_DAYS) {
    // Limpiar sesion expirada
    clearTempSession();
    return null;
  }
  
  return session;
}

// Limpiar sesion temporal.
// IMPORTANTE: NO llamar a getTempSession() aquí — getTempSession() invoca
// a clearTempSession() cuando detecta expiración, y si clearTempSession()
// vuelve a llamar a getTempSession() con el mismo storage antiguo entra en
// recursión infinita → RangeError: Maximum call stack size exceeded (solo
// visible cuando la sesión guardada ha superado SESSION_EXPIRY_DAYS).
// Leemos directo del storage sin pasar por getTempSession().
export function clearTempSession(): void {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      const session = JSON.parse(stored) as TempSession;
      // En produccion: borrar de Firebase Storage tambien
      console.log('Limpiando sesion temporal:', session.id);
    }
  } catch {
    // JSON.parse fallido: no importa, seguimos y removemos igual.
  }
  localStorage.removeItem(SESSION_KEY);
}

// Migrar sesion temporal a usuario registrado
export function migrateTempToUser(userId: string): TempSession | null {
  const tempSession = getTempSession();
  if (!tempSession) return null;
  
  // En produccion:
  // 1. Mover foto de /temp/{tempSession.id}/ a /users/{userId}/
  // 2. Crear pedido en Firestore asociado al userId
  // 3. Copiar config al pedido
  // 4. Limpiar temporal
  
  console.log('Migrando sesion', tempSession.id, 'a usuario', userId);
  
  // Limpiar temporal despues de migrar
  clearTempSession();
  
  return tempSession;
}

// Verificar si hay datos temporales pendientes
export function hasPendingTempData(): boolean {
  const session = getTempSession();
  return !!session && (!!session.fotoReferencia || !!session.config);
}

// Obtener dias restantes antes de expiracion
export function getDaysUntilExpiry(): number {
  const session = getTempSession();
  if (!session) return 0;
  
  const daysSinceActivity = (Date.now() - session.lastActivity) / (1000 * 60 * 60 * 24);
  return Math.max(0, Math.floor(SESSION_EXPIRY_DAYS - daysSinceActivity));
}

// Programar limpieza automatica (ejecutar al iniciar app)
export function scheduleCleanup(): void {
  const session = getTempSession();
  if (session) {
    const daysUntilExpiry = getDaysUntilExpiry();
    console.log(`Sesion temporal expira en ${daysUntilExpiry} dias`);
    
    if (daysUntilExpiry === 0) {
      clearTempSession();
    }
  }
}
